/**
 * NotificationDropdown — shared notification panel (header bell).
 * Static inbox UI; product-aware copy via productWorkspaceConfig.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useI18n } from '../../context/I18nContext';
import {
  WORKSPACE_PRODUCTS,
  detectWorkspaceProduct,
  getFundGeniusNotificationsPath,
  getProductNotificationConfig,
} from '../../config/productWorkspaceConfig';

const buildSelfRepairNotifications = (t) => [
  {
    id: 'n1',
    tab: 'inbox',
    title: t('notificationPanel.sample1Title'),
    body: t('notificationPanel.sample1Body'),
    time: '2 hours ago',
    unread: true,
  },
  {
    id: 'n2',
    tab: 'inbox',
    title: t('notificationPanel.sample2Title'),
    body: t('notificationPanel.sample2Body'),
    time: '5 hours ago',
    unread: true,
  },
  {
    id: 'n3',
    tab: 'offers',
    title: t('notificationPanel.sample3Title'),
    body: t('notificationPanel.sample3Body'),
    time: '1 day ago',
    unread: true,
  },
  {
    id: 'n4',
    tab: 'updates',
    title: t('notificationPanel.sample4Title'),
    body: t('notificationPanel.sample4Body'),
    time: '1 day ago',
    unread: false,
  },
  {
    id: 'n5',
    tab: 'updates',
    title: t('notificationPanel.sample5Title'),
    body: t('notificationPanel.sample5Body'),
    time: '3 days ago',
    unread: false,
  },
];

const NotificationDropdown = ({
  tokens,
  className = '',
  product: productProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('inbox');

  const product = productProp || detectWorkspaceProduct({ pathname: location.pathname });
  const notificationConfig = getProductNotificationConfig(product);
  const isFundGenius = product === WORKSPACE_PRODUCTS.FUNDGENIUS_HQ;

  const tabs = useMemo(() => {
    if (Array.isArray(notificationConfig.tabs) && notificationConfig.tabs.length) {
      return notificationConfig.tabs;
    }
    return [
      { id: 'inbox', label: t('notificationPanel.tabInbox') },
      { id: 'offers', label: t('notificationPanel.tabOffers') },
      { id: 'updates', label: t('notificationPanel.tabUpdates') },
    ];
  }, [notificationConfig, t]);

  const seedNotifications = useMemo(() => {
    if (notificationConfig.useI18n) {
      return buildSelfRepairNotifications(t);
    }
    return notificationConfig.notifications || [];
  }, [notificationConfig, t]);

  const [notifications, setNotifications] = useState(seedNotifications);

  useEffect(() => {
    setNotifications(seedNotifications);
    setActiveTab('inbox');
  }, [seedNotifications]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => n.unread).length,
    [notifications]
  );

  const filteredNotifications = useMemo(
    () => notifications.filter((n) => n.tab === activeTab),
    [notifications, activeTab]
  );

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
  };

  const handleViewAll = () => {
    if (notificationConfig.hasNotificationsRoute && notificationConfig.viewAllPath) {
      navigate(notificationConfig.viewAllPath);
      return;
    }
    if (isFundGenius) {
      const target = getFundGeniusNotificationsPath(location.pathname);
      if (notificationConfig.viewAllComingSoon) {
        toast.info(notificationConfig.viewAllComingSoon);
      }
      navigate(target);
      return;
    }
    navigate('/settings/notifications');
  };

  const panelBg = tokens.isDark ? 'rgba(7,28,21,0.98)' : 'rgba(255,255,255,0.98)';
  const panelBorder = tokens.isDark ? 'rgba(142,218,182,0.14)' : 'rgba(15,172,108,0.12)';
  const muted = tokens.isDark ? '#93AE9F' : '#6B7280';
  const primary = tokens.isDark ? '#EAF7F0' : '#111827';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`p-2 rounded-lg relative transition-all duration-200 hover:opacity-100 opacity-90 ${className}`}
          style={{
            background: tokens.isDark ? 'rgba(7,28,21,0.75)' : 'rgba(255,255,255,0.90)',
            border: `1px solid ${tokens.content.borderSubtle}`,
            color: tokens.topBar.text,
          }}
          aria-label={t('topBar.notifications')}
          data-testid="notifications-btn"
          data-product={product}
        >
          <Bell size={16} strokeWidth={2} aria-hidden />
          {unreadCount > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[8px] h-2 px-0.5 rounded-full"
              style={{ background: '#0FAC6C' }}
              aria-hidden
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[360px] max-w-[calc(100vw-2rem)] p-0 overflow-hidden border-0 shadow-none z-[80]"
        style={{
          background: panelBg,
          border: `1px solid ${panelBorder}`,
          borderRadius: '16px',
          boxShadow: tokens.isDark
            ? '0 12px 40px rgba(0,0,0,0.42)'
            : '0 12px 40px rgba(16,24,40,0.14)',
        }}
        data-testid="notification-dropdown-panel"
        data-product={product}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: panelBorder }}>
          <h3 className="text-sm font-bold" style={{ color: primary }}>
            {t('notificationPanel.title')}
          </h3>
          <button
            type="button"
            onClick={markAllRead}
            className="text-xs font-semibold transition-opacity hover:opacity-80"
            style={{ color: tokens.isDark ? '#56B988' : '#0D9F64' }}
            data-testid="mark-all-read-btn"
          >
            {t('notificationPanel.markAllRead')}
          </button>
        </div>

        <div
          className="flex gap-1 px-3 py-2 border-b"
          style={{ borderColor: panelBorder }}
          role="tablist"
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const tabUnread = notifications.filter((n) => n.tab === tab.id && n.unread).length;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className="flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                style={{
                  background: isActive
                    ? (tokens.isDark ? 'rgba(15,172,108,0.18)' : 'rgba(15,172,108,0.10)')
                    : 'transparent',
                  color: isActive ? (tokens.isDark ? '#8FE0B7' : '#0B8C58') : muted,
                }}
                data-testid={`notification-tab-${tab.id}`}
              >
                {tab.label}
                {tabUnread > 0 ? ` (${tabUnread})` : ''}
              </button>
            );
          })}
        </div>

        <div className="max-h-[280px] overflow-y-auto p-2">
          {filteredNotifications.length === 0 ? (
            <p className="text-center text-xs py-8" style={{ color: muted }}>
              {t('notificationPanel.emptyTab')}
            </p>
          ) : (
            filteredNotifications.map((item) => (
              <div
                key={item.id}
                className="p-3 rounded-xl mb-1 cursor-pointer transition-colors"
                style={{
                  background: item.unread
                    ? (tokens.isDark ? 'rgba(142,218,182,0.06)' : 'rgba(15,172,108,0.05)')
                    : 'transparent',
                }}
                data-testid={`notification-item-${item.id}`}
              >
                <div className="flex items-start gap-2">
                  {item.unread && (
                    <span
                      className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                      style={{ background: '#0FAC6C' }}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-snug" style={{ color: primary }}>
                      {item.title}
                    </p>
                    <p className="text-xs mt-0.5 leading-relaxed" style={{ color: muted }}>
                      {item.body}
                    </p>
                    <p className="text-[11px] mt-1" style={{ color: muted }}>
                      {item.time}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-2 border-t" style={{ borderColor: panelBorder }}>
          <button
            type="button"
            onClick={handleViewAll}
            className="w-full text-center text-sm font-semibold py-2.5 rounded-lg transition-opacity hover:opacity-80"
            style={{ color: tokens.isDark ? '#56B988' : '#0D9F64' }}
            data-testid="view-all-notifications-btn"
          >
            {t('notificationPanel.viewAll')}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationDropdown;
